'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import { useEffect, useRef } from 'react';
import { usePathname } from '@navigation';
import { derivePageType } from '@components/helpers/auto-page-view';
import { ArticleAnalyticsParams } from '@providers/analytics/analytics.types';

// Per-template page_view dispatcher. Used on routes that have richer
// metadata than the URL alone reveals (article category, team, publish
// date, etc.).
//
// AutoPageView in the locale layout already fires a baseline `page_view`
// on every route — those routes that mount this component are in
// AutoPageView's SKIP_AUTO_PATTERNS list so we don't double-emit.
//
// The component derives `page_type` from the URL via the same
// `derivePageType` function AutoPageView uses (so the enum stays
// consistent), but lets the caller override it with an explicit
// `page_type` prop if needed.
//
// `published_date` should be in ISO date format (YYYY-MM-DD). Strip any
// time component from Contentful's ISO datetime before passing — GA4
// treats this as a string event param, so the value just needs to be
// consistent and machine-readable.

const AnalyticsPageView = ({
  page_title,
  page_type,
  content_type,
  team,
  published_date,
  previous_page,
}: ArticleAnalyticsParams) => {
  const pathname = usePathname();
  const lastPushedRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const payload = {
      event: 'page_view',
      page_title,
      page_path: pathname,
      page_location: window.location.href,
      page_type: page_type ?? derivePageType(pathname),
      previous_page: previous_page ?? (document.referrer || undefined),
      ...(content_type !== undefined && { content_type }),
      ...(team !== undefined && { team }),
      ...(published_date !== undefined && { published_date }),
    };

    // Deduplicate identical fires (parent re-render with same data).
    const serialised = JSON.stringify(payload);
    if (lastPushedRef.current === serialised) return;
    lastPushedRef.current = serialised;

    sendGTMEvent(payload);
  }, [
    pathname,
    page_title,
    page_type,
    content_type,
    team,
    published_date,
    previous_page,
  ]);

  return null;
};

export default AnalyticsPageView;
